import { apiClient } from "../../lib/apiClient";

/** SH_ENVIRONMENT of the server — the install this admin belongs to. */
export type Environment = "production" | "development" | "local";

export type SourceId = "postgres" | "redis" | "redis_cache";
export type SourceStatus = "reachable" | "unreachable" | "not_configured";

export interface McpSource {
  id: SourceId;
  status: SourceStatus;
}

export interface McpStatus {
  enabled: boolean;
  environment: Environment;
  server_url: string;
  server_name: string;
  sources: McpSource[];
  statement_timeout_ms: number;
  max_rows: number;
}

export interface McpClient {
  id: string;
  client_name: string;
  client_host: string | null;
  redirect_host: string | null;
  user: { id: string; name: string; email: string };
  authorized_at: string;
  last_used_at: string | null;
}

/** The client as the public lookup describes it to the sign-in page. */
export interface OAuthClientSummary {
  id: string;
  name: string;
  host: string | null;
}

export interface AuthorizeRequest {
  client: {
    id: string;
    name: string;
    host: string | null;
    redirect_host: string;
  };
  user: { name: string; email: string; role: string };
  environment: Environment;
  allowed: boolean;
}

export type AuthorizeDecision = "approve" | "deny";

export const mcpStatusQueryKey = ["mcp"] as const;
export const mcpClientsQueryKey = ["mcp", "clients"] as const;

export async function getMcpStatus(): Promise<McpStatus> {
  const r = await apiClient.get<McpStatus>("/api/admin/mcp");
  return r.data;
}

export async function listMcpClients(): Promise<McpClient[]> {
  const r = await apiClient.get<{ clients: McpClient[] }>(
    "/api/admin/mcp/clients",
  );
  return r.data.clients;
}

export async function revokeMcpClient(id: string): Promise<void> {
  await apiClient.post(
    `/api/admin/mcp/clients/${encodeURIComponent(id)}/revoke`,
  );
}

export async function revokeAllMcpClients(): Promise<number> {
  const r = await apiClient.post<{ revoked: number }>(
    "/api/admin/mcp/clients/revoke-all",
  );
  return r.data.revoked;
}

export async function getOAuthClient(
  clientId: string,
): Promise<OAuthClientSummary> {
  const r = await apiClient.get<{ client: OAuthClientSummary }>(
    `/api/admin/oauth/client?client_id=${encodeURIComponent(clientId)}`,
  );
  return r.data.client;
}

/** `search` is `location.search` as is, leading "?" included. */
export async function getAuthorizeRequest(
  search: string,
): Promise<AuthorizeRequest> {
  const r = await apiClient.get<AuthorizeRequest>(
    `/api/admin/oauth/authorize${search}`,
  );
  return r.data;
}

/** `query` is the raw authorize query string without its leading "?". */
export async function decideAuthorizeRequest(
  query: string,
  decision: AuthorizeDecision,
): Promise<string> {
  const r = await apiClient.post<{ redirect_url: string }>(
    "/api/admin/oauth/authorize",
    { query, decision },
  );
  return r.data.redirect_url;
}
